import Commons from "oc-commons/Commons.js";
import Doms from "oc-commons/Doms.js";
import ListenerMixin from "oc-commons/ListenerMixin.js";
import {v2,v3,quat} from "./short3.js";
import AbstractStage from "./AbstractStage.js";
import Maths3 from "./Maths3.js";
import Scenes from "./Scenes.js";
import DeviceDetection from "oc-commons/DeviceDetection.js";
import Stats from "stats.js";
import toBlob from "canvas-to-blob";
import {saveAs} from "browser-filesaver";
import UILock from "oc-commons/UILock.js";
import "./patched/OrbitControls.oc.js";
import TWEEN from "@tweenjs/tween.js";
import OcAxisHelper from "./objects/OcAxisHelper.js";
import Textures from "./Textures.js";
import UniversalLoader from "./loader/UniversalLoader.js";
import "oc-threejs/patched/GLTFExporter.oc.js";
import "three/examples/js/postprocessing/EffectComposer.js";
import "three/examples/js/postprocessing/ShaderPass.js";
import "three/examples/js/postprocessing/RenderPass.js";
import "three/examples/js/postprocessing/SAOPass.js";
import "three/examples/js/shaders/SAOShader.js";
import "three/examples/js/shaders/DepthLimitedBlurShader.js";
import "three/examples/js/shaders/CopyShader.js";
import "three/examples/js/shaders/UnpackDepthRGBAShader.js";
import VisibleSwitchRenderPass from "./pass/VisibleSwitchRenderPass.js"
import "perfnow";
import "./scss/renderStats.scss";
import HBS from "./hbs/HBS.js";
import Maths from "oc-commons/Maths";
import Mimetypes from "oc-commons/Mimetypes";


toBlob.init();


/**
 * Die von AbstractStage abgeleitete Standard-Stage (siehe dort für einige weitere Methoden).
 * Diese Klasse ist sehr umfangreich, weil sie an einer Stelle die wesentlichen Methoden für die Manipulation der
 * Ansicht der Szene bereitstellt. AbstractStage ist dagegen so minimal wie möglich gehalten.
 *
 * Wesentliche Elemente sind das Erzeugen und Bereitstellen von
 *
 * - Scene
 * - WebGL-Renderer
 * - Camera
 * - OrbitControl
 * - Standard-Lichtern
 * - App-weiten Settings
 *
 * Die Methoden gruppieren sich nach den Themen
 *
 * - Hilfsmethoden für das Hinzufügen von Lichtern, Gitterboden und Koordinatenkreuz.
 * - Renderloop, Dirty-Flag, Verändern der Größe des Renderers und Rendern als Einzelbild
 * - Kamera-Manipulation, Speichern und Wiederherstellen der Kameraposition, Kamerafahrt, Wechsel Orthografisch/Perspektivisch
 * - Anzeige eines Hintergrundbildes
 * - Animator-Feature (Animationen werden als globaler ein/ausschaltbarer Zustand betrachtet,
 *   Objekte sorgen über eine Callback-Methode selbst für die Darstellung der einzelnen Animationsschritte)
 * - Anzeige der Frames-Per-Second-Info
 *
 */
export default class Stage extends ListenerMixin(AbstractStage) {

    /** */
    constructor(settings) {
        super(settings);
        this.name = 'STAGE';
        this.isDirty = false;
        this.stopRendering = false;
        this.renderingLoopRuns = false;
        this.renderOffline = false;
        this.renderLooping = false;
        this.stopAnimation =false;
        this.tweenTimer =0;
        this.tweenPrevTime = 0;
        this.renderOfflineIntervalMs = 100;

        // wie viele ms benötigte das letzte gerenderte Frame?
        this.lastRenderSceneMs = 0;

        this.stats = null;

        this.zoomScale = Math.pow(0.95, 0.3);
        this.locked = false;
        this.ignoreBindOrbitControlToYPlaneWithHeight = false;

        // when switching to this.innerWallMesh mode, save actual camera
        // position as it is going to change...
        this.saveCameraPosition = {
            object : {
                x : 0,
                y : 0,
                z : 0
            },
            target : {
                x : 0,
                y : 0,
                z : 0
            },
        }

        this.planeYHeight = null;
        this.perspectiveCamera = null;
        this.orthographicCamera = null;
        this.orthographicMode = false;

        this.scene = this.createScene();

        // export for https://github.com/jeromeetienne/threejs-inspector
        window.scene = this.scene;

        this.renderer = this.createRenderer();

        this.perspectiveCamera = this.createCamera();
        this.orthographicCamera = this.createOrthographicCamera();
        this.camera = this.perspectiveCamera;

        this.composerHook();
        this.orbitControl = this.createOrbitControl();

        if(this.settings.lightSettingsName == 'default') {
            this.createLights();
        }

        this._mainObject = this.scene;

        if (this.settings.rendererBackgroundImage) {
            this.addRendererBackgroundImage();
        }

        if (this.settings.animator) {
            this.addAnimator();
        }
        if (this.settings.showFramesPerSecondInfo) {
            this.showFramesPerSecondInfo();
        }
        if (this.settings.camera.bindToYPlaneWithHeight!==false) {
            this.bindOrbitControlToYPlaneWithHeight(this.settings.camera.bindToYPlaneWithHeight);
        }

        this.cameralock = new UILock((locked) => this.orbitControl.setEnabled(!locked));

        this.animations = {};
        this.mixer = {};

        this.resetOrbitControl();

        this.clock = new THREE.Clock();

        $('body').on('renderer-dirty',this.dirty);

        STAGE.addAnimator();

        this.dirty();
    }

    //--------------------------------------------------------------------------------------
    // Hauptobjekt
    //--------------------------------------------------------------------------------------

    /**
     * gibt das Hauptobjekt der Szene zurück.
     * Beispiel: Im Schrankplaner ist es der Schrank.
     * In Winnetoo ist es der gesamte Spielplatz.
     * Wird z.B. benutzt, um mit der Kamera draufzuzoomen.
     */
    get mainObject() {
        return this._mainObject;
    }

    set mainObject(value) {
        this._mainObject = value;
    }

    //--------------------------------------------------------------------------------------
    // Create-Methoden der Standardobjekte der Stage
    //--------------------------------------------------------------------------------------

    /** */
    createScene() {
        return new THREE.Scene();
    }

    /** */
    createCamera() {
        const cameraSettings = this.settings.camera;

        return new THREE.PerspectiveCamera(cameraSettings.fov,
            //divContainer.width() / divContainer.height(),
            1,
            cameraSettings.near, cameraSettings.far);
    }

    /** */
    createOrthographicCamera() {
        return new THREE.OrthographicCamera( -1, 1, 1, -1, STAGE.settings.camera.near, STAGE.settings.camera.far );
    }


    /** */
    createRenderer() {
        const renderer = new THREE.WebGLRenderer({
            antialias : this.settings.renderer.antialias,
            alpha: this.settings.renderer.alpha,
            // niemals true setzen -> performance!
            preserveDrawingBuffer : false,
        });


        // FIXME temporary workaround
        if (DeviceDetection.firefox) {
            console.warn('Achtung, das hier bitte bald rausnehmen, dient nur zum Ausschalten von überflüssigen Warnings im FF: DISABLING renderer.context.getShaderInfoLog, see https://github.com/mrdoob/three.js/issues/9716');
            renderer.getContext().getShaderInfoLog = () => '';
        }

        renderer.sortObjects=this.settings.renderer.sortObjects;

        renderer.getContext().canvas.addEventListener("webglcontextlost", event => {
            console.warn('webglcontextlost',event);
        }, false);

        if (this.settings.shadows.enabled) {
            renderer.shadowMap.enabled = true;
            renderer.shadowMap.type = THREE.PCFSoftShadowMap;
            renderer.logarithmicDepthBuffer = this.settings.renderer.logarithmicDepthBuffer;

            // FIXME THREE r90: Removed .shadowMap.renderReverseSided from WebGLRenderer. Set Material.shadowSide instead.
            // dumm daran ist nur, dass das eine Einstellung pro Material ist statt einer globalen Einstellung...
            // renderer.shadowMap.renderReverseSided=true;
            console.warn('THREE r90: Removed .shadowMap.renderReverseSided from WebGLRenderer. Set Material.shadowSide instead.');
        }

        // https://www.khronos.org/webgl/wiki/HandlingHighDPI
        // todo mal auf retina device checken! evtl. muss die canvas.with/height ebenfalls angepasst werden
        renderer.setPixelRatio( this.devicePixelRatio * (this.settings.ratioFactor)  );

        // renderer.setPixelRatio( window.devicePixelRatio );
        this.setClearColor(this.getClearColorFromSettings(), this.settings.renderer.clearColor.alpha, renderer);

        // set this for correct color output. The defaults of threejs are old and were never changed
        // https://discourse.threejs.org/t/whats-this-about-gammafactor/4264/2

        if (this.settings.renderer.toneMapping) {
            renderer.toneMapping = THREE[this.settings.renderer.toneMapping];
        }
        if (this.settings.renderer.pbrPipeline) {
            renderer.gammaFactor = 2.2;
            // renderer.gammaInput = true;
            renderer.toneMappingExposure = this.settings.renderer.exposure;
            // renderer.gammaOutput = true;
            renderer.outputEncoding = THREE.GammaEncoding;
            renderer.physicallyCorrectLights = true;
        }

        /**
         * @deprecated Jedes Projekt sollte über settings.renderer.pbrPipeline arbeiten
         */
        if (this.settings.renderer.deactivatePhysicalLights) {
            renderer.physicallyCorrectLights = false;
        }

        return renderer;
    }

    getClearColorFromSettings() {
        let c = this.settings.renderer.clearColor.color;
        if (c==null) {
            c = 0xffffff;
        }
        return new THREE.Color(c)
    }

    setClearColor(color=new THREE.Color(0xffffff), alpha=1,renderer=this.renderer) {
        if (alpha<0.999 && !STAGE.settings.renderer.alpha) {
            console.error('renderer.setClearColor with transparent alpha needs STAGE.settings.renderer.alpha=true, but is false. The generated image will not be transparent.');
        }
        renderer.setClearColor(color,alpha);
    }

    get devicePixelRatio() {
        return Math.min((window.devicePixelRatio||1), (this.settings.maxDevicePixelRatio||2));
    }

    /** */
    createOrbitControl() {
        const cameraSettings = this.settings.camera;

        const orbitControl = new THREE.OrbitControls(this.camera, this.canvas, ()=>this.dirty());

        orbitControl.polarAngleLocked = cameraSettings.polarAngle.locked;
        orbitControl.minPolarAngle = Maths3.angelDegToRad(cameraSettings.polarAngle.min);
        orbitControl.maxPolarAngle = Maths3.angelDegToRad(cameraSettings.polarAngle.max);

        orbitControl.minAzimuthAngle = Maths3.angelDegToRad(cameraSettings.azimuthAngle.min);
        orbitControl.maxAzimuthAngle = Maths3.angelDegToRad(cameraSettings.azimuthAngle.max);

        orbitControl.minCameraTargetPosition = cameraSettings.minCameraTargetPosition;
        orbitControl.maxCameraTargetPosition = cameraSettings.maxCameraTargetPosition;
        orbitControl.minCameraPosition = cameraSettings.minCameraPosition;
        orbitControl.maxCameraPosition = cameraSettings.maxCameraPosition;

        orbitControl.zoomSpeed = cameraSettings.zoomSpeed;
        orbitControl.rotateSpeed = cameraSettings.rotateSpeed;
        orbitControl.enablePan = !cameraSettings.disablePan;
        orbitControl.enableDamping = cameraSettings.enableDamping;
        orbitControl.dampingFactor = cameraSettings.dampingFactor;
        if (orbitControl.enableDamping){
            this.on('beforeRenderScene',()=>{
                if(orbitControl.update())
                    this.dirty();
            });
        }

        return orbitControl;
    }

    /** */
    createLights(name = STAGE.settings.lightSettingsName) {
        let lights = [];
        if (APP.sceneImporter) {
            if (Commons.isEmpty(name)) {
                name = "Default";
            }
            lights = APP.sceneImporter.createLights(name);
        }
        if (lights.length==0) {
            lights.push(this.addAmbientLightIntensity(0.5));
            lights.push(this.addDirectionalLightsAround(3,20,0.5));
        }
        return lights;
    }

    //--------------------------------------------------------------------------------------
    // Hilfsmethoden für das Hinzufügen von Lichtern
    //--------------------------------------------------------------------------------------

    /**
     * Erzeugt eine Anzahl von DirectionalLights um die Szenenmitte herum gleichmäßig verteilt
     * @param lights Anzahl der erzeugten Lichter
     * @param azimuth Höhe als Winkel in Grad (90°==Zenith)
     * @param intensityPerLight (0..1)
     */
    addDirectionalLightsAround(lights = 3, azimuth = 20, intensityPerLight = 1) {
        const v = v3(0, 0, -1);
        v.applyAxisAngle(Maths3.vector.x, azimuth * Math.PI / 180);
        this.addDirectionalLight(v.x, v.y, v.z, intensityPerLight);
        const polar = 360 / lights;
        for (let i = 1; i < lights; i++) {
            v.applyAxisAngle(Maths3.vector.y, polar * Math.PI / 180);
            this.addDirectionalLight(v.x, v.y, v.z, intensityPerLight);
        }
    }

    /**
     * Sucht in der Scene nach Lightquellen die dem Namen entsprechen
     * @param {String} name 
     * @returns Array
     */
    getLightByName(name = "") {
        return Scenes.filter(this.scene, o=>o.name == name && o.type.contains("Light")) || [];
    }

    /**
     * Erzeugt ein graues AmbientLight mit einer bestimmten Intensität von 0..1
     */
    addAmbientLightIntensity(intensity) {
        const b = Math.round(intensity * 255);
        return this.addAmbientLight(b << 16 | b << 8 | b);
    }

    /**
     * Erzeugt ein AmbientLigt einer bestimmten Farbe
     */
    addAmbientLight(color = 0xffffff,intensity=1) {
        //console.log('addAmbientLight(color=0x'+color.toString(16)+')');
        let light=new THREE.AmbientLight(color,intensity);
        this.scene.add(light);
        this.maybeAddLightHelper(light);
        return light;
    }

    maybeAddLightHelper(light) {
        if (APP.settings.stage.showDebugObjects) {
            Debugs.lightHelper(light);
        }
    }

    /**
     * Fügt ein DirectionalLight hinzu, das an einer bestimmten Position steht und auf den Nullpunkt gerichtet ist
     */
    addDirectionalLight(x, y, z, intensity = 1, color = 0xffffff) {
        // console.log('addDirectionalLight(position=('+x+','+y+','+z+'), intensity='+intensity+', color=0x'+color.toString(16)+')');
        const light = new THREE.DirectionalLight(color, intensity);
        light.position.set(x, y, z).normalize();

        if (this.settings.shadows.enabled) {
            light.castShadow = true;
        }
        this.scene.add(light);
        this.maybeAddLightHelper(light);
        return light;
    }

    /**
     * Fügt ein Spotlight hinzu
     */
    addSpotLight(x, y, z, intensity = 1, color = 0xffffff, distance = 0, angle = Math.PI / 3, target = v3()) {
        //console.log('addSpotLight(position=('+x+','+y+','+z+'), intensity='+intensity+', color=0x'+color.toString(16)+', distance='+distance+', angle='+angle+', target='+target+')');
        const light = new THREE.SpotLight(color, intensity, distance, angle);
        light.position.set(x, y, z);
        light.target.position.copy(target);

        if (this.settings.shadows.enabled) {
            light.castShadow = true;

            light.shadow.camera.near = 200;
            light.shadow.camera.far = 20000;
            light.shadow.camera.fov = 80;

            light.shadow.camera.bias = -0.0005;
            //light.shadowDarkness = 2;
            light.shadow.mapSize.width = 2048;
            light.shadow.mapSize.height = 2048;

            light.shadow.camera.near = 250;
            light.shadow.camera.far = 600;
            light.shadow.bias = -0.005;
            // light.shadow.mapSize.width = 2048;
            // light.shadow.mapSize.height = 2048;
        }

        this.scene.add(light);
        this.maybeAddLightHelper(light);
        return light;
    }

     /**
     *Fügt ein HemisphereLight hinzu
     */
    addHemisphereLight(x, y, z, intensity = 1, color = 0xffffff, color2=0xffffff){
        let light = new THREE.HemisphereLight(color, color2,intensity);
        light.position.set(x, y, z).normalize();
        this.scene.add(light);
         this.maybeAddLightHelper(light);
        return light;
    }

    //--------------------------------------------------------------------------------------
    // Hinzufügen von Standardobjekten
    //--------------------------------------------------------------------------------------

    /**
     * Kurzschreibweise, fügt ein Object zur Scene hinzu. Gibt das Object außerdem zurück.
     */
    add(object3d) {
        if (object3d instanceof THREE.Object3D) {
            this.scene.add(object3d);
        }
        return object3d;
    }

    /**
     * Fügt einen grauen "Boden" hinzu, der wie ein Millimeterpapier karriert ist.
     * Defaultmäßig empfängt der Boden keinen Schatten
     */
    addGroundScalePaper(radius=100,receiveShadow=false) {
        const geometry = new THREE.CircleGeometry( radius, 30 );
        const map = Textures.createScalePaperTexture({repetition: radius*2});

        const material = receiveShadow ? new THREE.MeshLambertMaterial({map}) : new THREE.MeshBasicMaterial({map});
        material.receiveShadow = receiveShadow;

        const ground = new THREE.Mesh( geometry, material );

        const plane = this.settings.anchorPlane || Maths3.plane.y0;
        const originOnPlane = plane.projectPoint(v3(0,0,0),v3());
        ground.raycastVisibleTags = [];
        ground.quaternion.setFromUnitVectors(Maths3.vector.z, plane.normal);
        ground.position.copy(originOnPlane);
        ground.name = 'ground';
        this.scene.add( ground );
        return ground;
    }

    /**
     * Fügt ein Koordinatenkreuz im Nullpunkt hinzu mit Länge 1 und beschrifteten Achsen
     */
    addAxisHelper(size=1) {
        const axisHelper = new OcAxisHelper(size);
        axisHelper.raycastVisibleTags = [];
        axisHelper.name = "coordinateOffset";
        this.scene.add(axisHelper);
        return axisHelper;
    }

    //--------------------------------------------------------------------------------------
    // renderer
    //--------------------------------------------------------------------------------------

    /**
     * fügt den Renderer als Kind zu einem DOM-Element hinzu
     */
    appendRendererCanvasTo(container) {
        // blende den Canvas vorübergehend aus,
        // weil onResize nach dem DOM-Attach von Canvas
        // erst mit ein paar Millisekunden Verzögerung aufgerufen wird, und
        // dann das Canvas für einen Moment klein dargestellt würde
        this.canvas.hide();
        container.append(this.canvas);
        this.addContainerResizeListener(container);
    }

    /**
     * fügt einen Resize Listener zum Container hinzu
     * @param container
     */
    addContainerResizeListener(container) {
        let canvasIsHidden = true;
        Doms.offResize(container);
        Doms.onResize(container, ()=>{
            this.setRendererToSize();
            if (canvasIsHidden) {
                this.canvas.show();
                canvasIsHidden = false;
            }
            // Bugfix für Anzeige der Tastatur auf Android => Sprites wurden nicht mehr richtig skaliert
            _.delay(()=>this.dirty(),500);
        });
    }

    /**
     * erzeugt ein DOM-Element mit dem Renderer als Kind
     */
    createRendererCanvasContainer() {
        const container = Doms.div('.threejsRenderer');
        this.appendRendererCanvasTo(container);
        return container;
    }

    /** */
    setRendererToSize(width,height) {
        let renderSize = this.getRenderSize();
        width = width || renderSize.width;
        height = height || renderSize.height;
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this._setRenderSize(width, height);
        this.dirty();
    }

    /** */
    getRenderSize() {
        const element = this.canvas;
        const parent = element.parent();
        return {
            width: parent.width() || 2,
            height: parent.height() || 2
        };
    }

    /**
     * @private
     */
    _setRenderSize(width, height) {
        this.renderer.setPixelRatio( this.devicePixelRatio * (this.settings.ratioFactor)  );
        this.renderer.setSize(width, height,true);
        if (this.composer) {
            this.composer.setSize(width * 1, height * 1);
        }
    }

    /**
     * rendered ein einzelnes Frame
     */
    renderSceneOnce() {
        this.isDirty=false;

        this.renderer.autoClear = false;
        this.renderer.clear();

        this.trigger('beforeRenderScene');

        this.onBeforeRenderStage();

        const t0 = performance.now();
        this._render();
        const t1 = performance.now();
        this.lastRenderSceneMs = t1-t0;

        this.onAfterRenderStage();

        this.renderScondScene();

        this.trigger('afterRenderScene');
        // JVI Das folgende ersetze ich durch diesen Tipp: https://github.com/tweenjs/tween.js/pull/53
        // Dann geht die Animation an der Stelle weiter, an der sie pausiert wurde.
        // if (!this.stopAnimation) {
        //     TWEEN.update();
        // }

        if ( this.stopAnimation === false ) {

            this.tweenTimer += t1 - this.tweenPrevTime;

        }
        this.tweenPrevTime = t1;
        TWEEN.update( this.tweenTimer );
    }

    renderScondScene() {
        if (this.secondPassScene!=null) {
            this.renderer.clearDepth();
            this.renderer.render( this.secondPassScene, this.camera );
        }
    }

    get tweenStartNowTime(){
        return this.tweenTimer+performance.now()-this.tweenPrevTime;
    }

    /**
     * zentrale Methode, um ein das Neurendern eines Frames auszulösen
     */
    dirty() {
        this.isDirty = true;
    }

    /**
     * startet den Render Loop (der Renderloop ist eine Endlosschleife, in der immer wieder
     * das Bild neu gezeichnet wird, falls das Dirty-Flag true ist)
     */
    startRenderLoop() {
        if (this.renderingLoopRuns) {
            return;
        }
        this.renderingLoopRuns = true;
        console.log('STAGE.startRenderLoop');
        requestAnimationFrame(()=>this._renderLoop());
    }

    /**
     * Hält den Renderloop an
     */
    stopRenderLoop() {
        this.stopRendering = true;
    }

    /**
     * setzt den Renderer in Offline-Modus, d.h. es wird auch dann ein Bild gezeichnet,
     * wenn das Fenster oder der Tab nicht sichtbar sind
     */
    setRenderOffline(offline) {
        this.renderOffline = offline;
    }

    /**
     * interne Methode, der eigentliche Rendervorgang
     */
    _renderLoop(timestamp) {
        // const animations = [];
        // this.scene.traverse(c => {if(c.animationPlaying)animations.push(c)});
        if(!Commons.isEmpty(this.mixer) && this.mixer.animationPlaying) {
            this.stopRendering = false;
            this.renderLooping = true;
            this.mixer.update(this.clock.getDelta());
        }
        if (this.stopRendering) {
            this.stopRendering = false;
            this.renderingLoopRuns = false;
            return;
        }
        if (this.isDirty || this.renderLooping) {
            this.renderSceneOnce();
        }
        const doit = () => {
            if (this.renderOffline) {
                // ohne requestAnimationFrame kann auch gerendered werden,
                // wenn das Window nicht angezeigt wird => für PDF-Druck
                _.delay(()=>this._renderLoop(),this.renderOfflineIntervalMs);
            } else {
                requestAnimationFrame(()=>{
                    this._renderLoop()
                });
            }
        }
        if (this.settings.lowPerformanceDevice) {
            console.log('rendered delayed!');
            _.delay(doit,50);
        } else {
            doit();
        }
    }

    /**
     * rendered ein Bild und gibt es als Canvas zurück
     */
    renderToCanvas() {
        this.renderer.clear();
        this.trigger('beforeRenderScene');
        this._render();
        return this.canvas;
    }

    /**
     * @private
     */
    _render() {
        if (this.composer && this.useComposer){
            this.composer.render();
        }
        else {
            this.renderer.render(this.scene, this.camera);
        }
    }

    /**
     * rendered ein Bild und gibt es als Base64-Encdoded JPG zurück
     */
    renderToDataUrl() {
        // performance ist hier wesentlich besser als bei preserveDrawingBuffer!
        // https://stackoverflow.com/questions/30628064/how-to-toggle-preservedrawingbuffer-in-three-js
        return this.renderToCanvas()[0].toDataURL("image/jpeg");
    }

    /**
     * rendered ein Bild und gibt es als File-Blob zurück, welches z.B. als File an FormData angefügt werden kann
     * für einen Upload.
     * @example
     * const formData = new FormData();
     * STAGE.renderToBlob('image/jpeg',(blob)=>{
     *    formData.append("file", blob, 'myImage.jpg');
     *    ...
     * });
     */
    renderToBlob(mimetype,callback) {
        //const canvas = this.renderToCanvas();
        this.onOnce('afterRenderScene',()=>this.canvas[0].toBlob(callback,mimetype));
        this.renderSceneOnce();
    }

    /**
     * rendered ein Bild und speichert es lokal ab (download)
     */
    renderToDownloadImage(filename) {
        const mimetype = Mimetypes.getMimetypeOfFilename(filename);
        this.onOnce('afterRenderScene',()=>
            this.canvas[0].toBlob((blob) => saveAs(blob, filename, mimetype),mimetype)
        );
        this.renderSceneOnce();
    }

    /**
     * exportiert die Scene als GLB Blob
     */
    gltfToBlob(callback, parameters, object) {
        new THREE.GLTFExporter().parse(
            object,
            (data) => callback(new Blob([data], {type: 'application/octet-stream'})),
            Commons.defaultsDeepSafe(parameters,{
                binary: true,
                forceIndices: true,
                forcePowerOfTwoTextures: true
            }));
    }

    /**
     * Lädt die Szene als GLB Datei herunter
     */
    downloadGlb(filename='scene.glb', parameters, object = STAGE.scene) {
        this.gltfToBlob((blob)=>saveAs(blob, filename, 'application/octet-stream'),parameters, object);
    }

    // Startet einen statischen Rendervorgang der aktuellen Kamera auf der renderpipeline mit anschließendem Download
    // async renderToBlenderBlob(x, y, lightfactor, o3) {

    //     const getPNGFromTMPURL = async (url) => {
    //         const response = await fetch(url, {
    //             method: "GET",
    //         });

    //         const image = await response.blob();

    //         return image;
    //     }

    //     const pipelineCall = async (file, renderData) => {
    //         const formData = new FormData();
    //         formData.append("glb", file);
    //         formData.append("json", renderData);

    //         const response = await fetch("http://api.objectcode.de/public/api/staticRender", {
    //             method: "POST",
    //             body: formData,
    //         });

    //         const imageurl = await response.text();
    //         const image = await getPNGFromTMPURL(imageurl);
    //         return image;
    //     }

    //     const downloadFile = (file) => {
    //         const downloadURL = URL.createObjectURL(file);
    //         const link = document.createElement("a");
    //         link.href = downloadURL;
    //         link.download = file.name;
    //         link.click();
    //     }

    //     let clone = _.cloneDeep(o3);

    //     let camera = new THREE.PerspectiveCamera(45, 1, 0.01, 1000);
    //     camera.position.copy(STAGE.camera.position);
    //     let lookAtVector = new THREE.Vector3(0,0,0);
    //     lookAtVector.applyQuaternion(STAGE.camera.quaternion);

    //     camera.lookAt(lookAtVector);
    //     camera.fov = STAGE.camera.fov;

    //     clone.add(camera);
    //     this.gltfToBlob(async (blob) => {
    //         const resultImage = await pipelineCall(
    //             blob,
    //             new Blob(
    //                 [
    //                     JSON.stringify({
    //                         name: "renderData.json",
    //                         resolution_x: parseInt(
    //                             x
    //                         ),
    //                         resolution_y: parseInt(
    //                             y
    //                         ),
    //                         light_factor: 1,
    //                         samples: parseInt(
    //                             lightfactor
    //                         ),
    //                     }),
    //                 ],
    //                 { type: "application/json" }
    //             )
    //         );

    //         const resultImage = blob;

    //         resultImage.name = "rendering.glb";
    //         downloadFile(resultImage);

    //     }, {googleARUVMapping: false}, clone);
    //     clone = null;
    // }


    /**
     * Friert vordergründig den Renderer mit einem Blur-Effekt ein,
     * damit im Hintergrund andere Ansicht gerendert werden können,
     * z.B. für das Erzeugen von Ansichten für das PDF.
     * Zurückgegeben wird ein Object mit einer hide() - Methode,
     * welche den Blur-Modus beendet und die normale Ansicht
     * wiederherstellt.
     * @returns {{hide: hide}}
     */
    freezeRendererAndBlur() {
        const blurOutGFX = () => {
            const element = this.canvas;
            const imageData = this.renderToDataUrl();
            const blurDiv = $('<div>');
            const blurParent = $('<div>');
            blurDiv.css({
                position : 'fixed',
                top : 0,
                left : 0,
                right : 0,
                bottom : 0,
                'background-image' : 'url(' + imageData + ')',
                '-webkit-filter' : 'blur(5px)',
            });
            blurParent.css({
                position : 'fixed',
                width : element.width(),
                height : element.height(),
                top : element.offset().top,
                left : element.offset().left,
                'z-index' : 100,
                'overflow' : 'hidden',
                '-webkit-transform' : 'translate3d(0, 0, 0)',
                '-moz-transform' : 'translate3d(0, 0, 0)',
                '-ms-transform' : 'translate3d(0, 0, 0)',
                '-o-transform' : 'translate3d(0, 0, 0)',
                'transform' : 'translate3d(0, 0, 0)',
            });
            blurParent.append(blurDiv);
            blurParent.hideDialog = () => {
                blurParent.remove();
            }
            $('body').append(blurParent);
            return blurParent;
        }
        if (!DeviceDetection.isLowPerformanceDevice()) {
            return blurOutGFX();
        }
        return {
            hide: ()=>{}
        }
    }

    /**
     * Versucht, den Renderer / WebGL-Context abzuräumen.
     * @see https://stackoverflow.com/questions/21548247/clean-up-threejs-webgl-contexts
     */
    dispose() {
        $('body').off('renderer-dirty',this.dirty);
        if (this.animator) {
            this.animator.stop();
        }
        this.stopRenderLoop();
        this.renderer.forceContextLoss();
        this.canvas.remove();
        // this.renderer.context = null;
        this.renderer.domElement = null;
        this.renderer = null;
    }

    //--------------------------------------------------------------------------------------
    // Camera
    //--------------------------------------------------------------------------------------

    /**
     * Gibt eine CameraConfig entsprechend eines interpretierten Eintrags der Camera-Tabelle zurück
     */
    getCameraConfigFromDescription(cameraDescription_or_nameFromTable) {
        let description = _.isString(cameraDescription_or_nameFromTable)
            ? APP.metadata.Camera.find(cd => cd.name == cameraDescription_or_nameFromTable)
            : cameraDescription_or_nameFromTable;
        if (description==null) {
            return null;
        } else {
            description = Commons.defaultsDeepSafe(description,{
                "Typ": "Zoom",
                "position": v3(0,0,10),
                "target": v3(),
                "zoomTargetRadius": 0,
                "zoomDirection": v3(0,0,1),
                "zoomDirectionWorld": true,
                "extraZoom": 1,
                "panningAfter": v3(0,0,0),
                "orthographic": false,
                "up": v3(0,1,0),
            });
        }
        let target = Scenes.findBySelector(STAGE.scene, description.target);
        if (target==null) {
            console.warn("getCameraConfigFromDescription found no target",description.target);
            target = v3();
        }
        let result = null;
        const targetIsVector = target instanceof THREE.Vector3;
        if (description.Typ == "Position") {
            if (!targetIsVector) {
                target = target.position;
            }
            result = {
                object: description.position.clone().add(description.panningAfter),
                target: target.clone().add(description.panningAfter),
                up: description.up,
                orthographic: description.orthographic
            };
        } else {
            let newPosition = null;
            let newLookAt = null;
            if (targetIsVector) {
                const sphere = new THREE.Sphere(target, description.zoomTargetRadius);
                const result = this.getCameraVectorsAsZoomTo(sphere, description.zoomDirection, description.extraZoom);
                if (result) {
                    [newLookAt, newPosition] = result;
                }
            } else {
                const box = target.jaco?.box ? target.jaco.box : Scenes.boundingBoxLocalMatrix(target);
                const direction = description.zoomDirection.clone();
                if (!description.zoomDirectionWorld) {
                    direction.applyQuaternion(target.getWorldQuaternion());
                }
                const result = this.getCameraConfigAsZoomToBox(box, direction, description.extraZoom);
                if (result) {
                    [newLookAt, newPosition] = result;
                }
            }
            if (newPosition==null || newLookAt==null) {
                return null;
            }
            result = {
                object: newPosition.clone().add(description.panningAfter),
                target: newLookAt.clone().add(description.panningAfter),
                up: description.up||v3(0,1,0),
                orthographic: description.Typ == "Orthographic",
                disableRotate: description.disableRotate
            };
        }
        if (description.Typ == "Orthographic") {
            if (description.zoomTargetRadius>0) {
                result.radius = description.zoomTargetRadius;
            } else {
                result.radius = target instanceof THREE.Object3D
                    ? Scenes.boundingSphere(target).radius
                    : 1;
            }
        }
        return result;
    }

    /**
     * Setzt die Kamera entsprechend eines Eintrags der Camera-Tabelle
     */
    setCameraFromDescription(cameraDescription_or_nameFromTable) {
        const config = this.getCameraConfigFromDescription(cameraDescription_or_nameFromTable);
        this.setCameraConfig(config);
    }

    /**
     * speichert intern die aktuelle Kameraposition
     */
    saveCameraPos() {
        this.saveCameraPosition = this.getCurrentCameraConfig();
        return this.saveCameraPosition;
    }

    /**
     * stellt die gepseicherte Kameraposition wieder her
     */
    restoreCameraPos() {
        this.setCameraConfig(this.saveCameraPosition);
    }

    /**
     * gibt die zuletzt gespeicherte Camera Config zurück
     */
    getSavedCameraPos() {
        return this.saveCameraPosition;
    }

    /**
     * gibt die momentanen Cameraeinstellungen als JSON zurück
     */
    getCurrentCameraConfig() {
        const cameraConfig = {
            object : {},
            target : {},
            up : {},
        };
        const axis = [ 'x', 'y', 'z' ].forEach(dim => {
            cameraConfig.object[dim] = this.orbitControl.object.position[dim];
            cameraConfig.target[dim] = this.orbitControl.target[dim];
            cameraConfig.up[dim] = this.orbitControl.object.up[dim];
        });
        return cameraConfig;
    }

    /**
     * setzt die Cameraeinstellungen von einer JSON-Config
     */
    setCameraConfig(cameraConfig) {
        if (!cameraConfig) {
            return;
        }

        this.orbitControl.enableRotate = !cameraConfig.disableRotate;

        const shouldBeOrthographicMode = cameraConfig.orthographic||false;
        this.setOrthographicMode(shouldBeOrthographicMode,{
            object: null,
            directionOfCamera: cameraConfig.zoomDirection,
            up: cameraConfig.up,
            border: cameraConfig.border ?? 1,
            canvasSize:cameraConfig.canvasSize,
            sphere: new THREE.Sphere(cameraConfig.target,cameraConfig.radius),
        });
        if (!shouldBeOrthographicMode) {
            if (cameraConfig.zoomTo) {
                const direction = cameraConfig.direction ? v3(cameraConfig.direction) : null;
                this.zoomTo(cameraConfig.zoomToTarget|| this.mainObject,direction,cameraConfig.extraZoom);
            } else {
                [ 'x', 'y', 'z' ].forEach(dim => {
                    this.orbitControl.object.position[dim] = cameraConfig.object[dim];
                    this.orbitControl.target[dim] = cameraConfig.target[dim];
                    if (cameraConfig.up) {
                        this.orbitControl.object.up[dim] = cameraConfig.up[dim];
                    }
                });
            }
            this.dirty();
            this.orbitControl.update();
        }
    }

    /**
     * gibt eine Differenz als Länge zwischen zwei Camera-Konfigs zurück
     * @param cameraConfigA
     * @param cameraConfigB
     */
    getCameraConfigDistance(cameraConfigA,cameraConfigB) {
        return Math.max(
            v3(cameraConfigA.object).distanceTo(v3(cameraConfigB.object)),
            v3(cameraConfigA.target).distanceTo(v3(cameraConfigB.target))
        );
    }

    /**
     * ist eine CameraConfig wirklich verschieden von der aktuellen Kamera?
     * Damit kann man unnötige Kamera-Animationen vermeiden
     */
    isDifferentFromCurrentCameraConfig(cameraConfig,epsilon=0.2) {
        const currentConfig = STAGE.getCurrentCameraConfig();
        return this.getCameraConfigDistance(currentConfig,cameraConfig) > epsilon;
    }


    /**
     * setzt eine bestimmte Kameraeinstellung und Hintergrundfarbe des Renderers und gibt ein Callback
     * zurück, mit dem die alten Einstellungen wiederhergestellt werden können. Ideal für das
     * erzeugen eines Produkt-Thumbnails.
     * @example
     * const undo = STAGE.setCameraAndRenderConfigAndReturnUndo({
     *   // die JSON-CameraConfig
     *   object : {x:0,y:0,z:100},
     *   target : {x:0,y:0,z:0},
     *   up : {x:0,y:1,z:0},
     *   // Hintergrund des Renderers
     *   backgroundColor: 0xFFFFFF,
     *   backgroundAlpha: 1,
     *   // Objekte, die ausgeblendet werden sollen
     *   hideObjectsByName: ['ground','coordinateOffset','BoxRuler'],
     *   // Callbacks, mit denen weitere Änderungen an der Stage vorgenommen werden können
     *   prepareStage: ()=>{},
     *   restoreStage: ()=>{},
     * });
     * _.delay(function() {
     *    const imageData = Doms.getAsJpgBase64Encoded(STAGE.renderToDataUrl());
     *    undo();
     *    // jetzt z.B, Bild speichern...
     * }, 1000);
     */
    setCameraAndRenderConfigAndReturnUndo(cameraConfig) {

        const undoConfig = {
            camera: this.getCurrentCameraConfig(),
            ignoreBindOrbitControlToYPlaneWithHeight: this.ignoreBindOrbitControlToYPlaneWithHeight,
            showDebugObjects: APP.settings.showDebugObjects,
            showObjects: [],
            clearColor: this.renderer.getClearColor().getHex(),
            clearAlpha: this.renderer.getClearAlpha(),
            backgroundTexture: this.scene.background,
        }

        this.ignoreBindOrbitControlToYPlaneWithHeight = true;
        this.setCameraConfig(cameraConfig);
        APP.settings.showDebugObjects = false;
        cameraConfig.hideObjectsByName.forEach(name=>{
            Scenes.getObjectsByNameRecursive(name).forEach(object=>{
                if (object && object.visible) {
                    object.visible=false
                    undoConfig.showObjects.push(object);
                }
            });
        });

        this.setClearColor( cameraConfig.backgroundColor, cameraConfig.backgroundAlpha );
        if (cameraConfig.prepareStage) {
            cameraConfig.prepareStage();
        }
        if (this.scene.background && !cameraConfig.showBackgroundImage)
            this._removeBackgroundImage(false);

        return ()=>{
            this.setCameraConfig(undoConfig.camera);
            this.ignoreBindOrbitControlToYPlaneWithHeight = undoConfig.ignoreBindOrbitControlToYPlaneWithHeight;
            APP.settings.showDebugObjects = undoConfig.showDebugObjects;
            undoConfig.showObjects.forEach(o=>o.visible=true);
            this.setClearColor( undoConfig.clearColor, undoConfig.clearAlpha );
            if (cameraConfig.restoreStage) {
                cameraConfig.restoreStage();
            }
            this._setBackgroundTexture(undoConfig.backgroundTexture);
        }
    }

    /**
     * setzt die Kamera in den Orthografischen Modus (Isometrische Ansicht bzw. ohne Pespektive)
     * oder wieder zurück
     * @param on true = Ortografisch, false = Perspektivisch
     * @example
     * STAGE.setOrthographicMode(true,{
     *      // Objekt auf das geschaut wird. Das Objekt wird komplett ins Blickfeld gerückt. Muss gesetzt sein.
            object: someObject,
            // Richtung in der die Kamera guckt
            directionOfCamera: v3(0,-1,0),
            // Wo ist oben?
            up: v3(-1,0,0),
     * });
     */
    setOrthographicMode(on,config) {
        const changed = this.orthographicMode != on;
        this.orthographicMode = on;

        if (changed) {
            if (on) {
                this.scene.remove( this.perspectiveCamera );
                this.scene.add( this.orthographicCamera );
                this.camera = this.orthographicCamera;
            } else {
                this.scene.remove( this.orthographicCamera );
                this.scene.add( this.perspectiveCamera );
                this.camera = this.perspectiveCamera;
            }
            this.cameralock.setLocked(on);
            this.dirty();
        }

        if (on) {
            this.setOrthographicCameraConfig(config);
        }

    }

    /** */
    setOrthographicCameraConfig(config) {

        config = Commons.defaultsDeepSafe({},config,{
            object: null,
            directionOfCamera: v3(0,-1,0),
            up: v3(-1,0,0),
            border: 10,
            sphere: null
        });

        console.log(config);


        const sphere = config.sphere || Scenes.boundingSphere(config.object);

        let {width,height} = config.canvasSize || this.getRenderSize();
        const ar = width / height;
        const r = sphere.radius+config.border;
        const w = r * ar;
        const h = r;

        const cameraSettings = this.settings.camera;

        this.orthographicCamera.left = -w;
        this.orthographicCamera.right = w;
        this.orthographicCamera.top = h;
        this.orthographicCamera.bottom = -h;

        this.orthographicCamera.updateProjectionMatrix();

        this.camera.position.copy(sphere.center.clone().add(config.directionOfCamera.clone().multiplyScalar(-(cameraSettings.far-cameraSettings.near)/2)));
        this.camera.up.copy(config.up);
        this.camera.lookAt(sphere.center);

        this.dirty();
    }

    /**
     * animiert die Kamera flüssig zu einer bestimmten Kameraposition
     * Sollte das erste Argument leer sein, wird die Kamera auf die initiale Position gefahren
     * @example
     * STAGE.animateCameraTo({
     *      // die JSON-CameraConfig
     *      object : {x:0,y:0,z:100},
     *      target : {x:0,y:0,z:0},
     *      up : {x:0,y:1,z:0},
     *    },{
     *      onComplete: ()=>{},
     *      onStep: (t)=>{},
     *      duration: 2000,
     *      locked: true,
     *      easing: TWEEN.Easing.Sinusoidal.InOut,
     * });
     */
    animateCameraTo(to,options) {
        options = Commons.defaultsDeepSafe({},options,{
            onComplete: ()=>{},
            onStep: (t)=>{},
            duration: 2000,
            locked: true,
            easing: TWEEN.Easing.Sinusoidal.InOut,
        });
        to = Commons.defaultsDeepSafe({},to,{
           object:this.settings.camera.position,
           target: this.settings.camera.target,
           up: v3(0,1,0)
        });

        const camera = this.camera;
        const from = _.cloneDeep(this.getCurrentCameraConfig());

        const to_ = Commons.flattenObject(to);
        to_.t = 1;
        const from_ = Commons.flattenObject(from);
        from_.t = 0;

        if (options.locked) {
            this.cameralock.setLocked(true);
        }

        const tween = new TWEEN.Tween(from_)
            .to(to_,options.duration)
            .easing(options.easing)
            .onUpdate(() => {
                const unflat = Commons.unflattenObject(from_);

                camera.up.copy(unflat.up);
                this.orbitControl.target.copy(unflat.target);
                this.orbitControl.object.position.copy(unflat.object);
                this.orbitControl.update();

                //camera.position.copy(unflat.object);
                //camera.lookAt(unflat.target);

                options.onStep(unflat.t);
                this.dirty();
            }).onComplete(() => {
                this.dirty();
                if (options.locked) {
                    this.cameralock.setLocked(false);
                }
                options.onComplete();
            }).start(this.tweenStartNowTime);
        this.dirty();
    }

    /**
     * gibt Camera-Lookat und Position Vektor zurück, um ein gegebenes Objekt komplett auf dem
     * Bildschirm darzustellen, mittels einer BoundingSphere
     * @param {(THREE.Object3D|THREE.Sphere|Array<THREE.Object3D>)} objectsOrSphere das Objekt oder die THREE.Sphere, auf die gezoomt wird
     * @param {*} [direction=null] die Richtung, in der das Objekt betrachtet werden soll
     * @param {number} [extraZoom=1] ein Extra-Zoomfaktor, der nachträglich hinzukommt
     * @param {*} [newPosition=v3()] Rückgabevektor, in der die neue Position gespeichert wird
     * @param {*} [newLookAt=v3()] Rückgabevektor, in der die neue Lookat gepseichert wird
     * @returns [newLookAt,newPosition] oder null, falls das Objekt keine Ausdehnung hat
     */
    getCameraVectorsAsZoomTo(objectsOrSphere,direction=null,extraZoom=1,newPosition=v3(),newLookAt=v3()) {
        const sphere = objectsOrSphere instanceof THREE.Sphere ? objectsOrSphere : Scenes.boundingBox(objectsOrSphere,o=>o.visible).getBoundingSphere();
        if (sphere.radius<0.0000001 || !_.isFinite(sphere.radius)) {
            return null;
        }
        const old = this.orbitControl.object.position.clone();

        if (this.planeYHeight && !this.ignoreBindOrbitControlToYPlaneWithHeight) {
            const centerOnPlane = this.planeYHeight.projectPoint(sphere.center,v3());
            sphere.radius += centerOnPlane.distanceTo(sphere.center);
            sphere.center = centerOnPlane;
        }

        const lookAt = sphere.center;

        if (!direction) {
            direction = old.sub(lookAt);
        }
        direction = direction.clone().normalize();

        const fov = this.camera.fov || 45;
        const distance = sphere.radius / Math.tan(fov/2*Math.PI/180);

        newLookAt.copy(lookAt);
        newPosition.copy(lookAt.add(direction.multiplyScalar(distance*extraZoom)));

        return [newLookAt,newPosition];
    }

    /**
     * gibt Camera-Lookat und Position Vektor zurück, um ein gegebenes Objekt komplett auf dem
     * Bildschirm darzustellen
     * @param {THREE.Box3} box  boundingBox des Objektes auf die gezoomt wird
     * @param {?THREE.Vector3} direction die Richtung, in der das Objekt betrachtet werden soll
     * @param {number} [extraZoom=1] ein Extra-Zoomfaktor, der nachträglich hinzukommt
     * @param {THREE.Vector3} [newPosition=v3()] Rückgabevektor, in der die neue Position gespeichert wird
     * @param {THREE.Vector3} [newLookAt=v3()] Rückgabevektor, in der die neue Lookat gepseichert wird
     * @returns [newLookAt,newPosition] oder null, falls das Objekt keine Ausdehnung hat
     */
    getCameraConfigAsZoomToBox(box,direction,  extraZoom=1, newPosition=v3(),newLookAt=v3()){
        let k=1;
        newLookAt.copy(box.getCenter());
        // const invalidBox=Scenes.axisNames.some(a=>!isFinite(box.min[axis])||!isFinite(box.max[axis]));

        if (!direction) {
            const old = this.orbitControl.object.position.clone();
            direction = old.sub(newLookAt);
        }


        if (box.isEmpty()){
            console.warn('Empty Box as zoomto');
            return [newLookAt,v3(newLookAt).sub(direction)];
        }
        const corners=box.getCorners();
        corners.forEach(c=>c.sub(newLookAt)); //solve Problem with center=0,0,0;
        const angleCenterRayOuterRay=STAGE.settings.camera.fov/180*Math.PI/2;

        const checkAllCorners = (k)=>{
            const base=v3(direction).multiplyScalar(k);
            const baselength=base.length();
            return corners.every((corner)=>{
                const baseCorner=v3(corner).add(base);
                const baseCornerLength=baseCorner.length();
                return angleCenterRayOuterRay>Math.acos( baseCorner.dot(base)/baselength/baseCornerLength );
            })
        }
        let check=checkAllCorners(k);
        let step=1;
        let factor=check?-1:1;


        while (step>0.00001){
            let temp=check;
            while (temp==check){
                k+=factor*step;
                temp=checkAllCorners(k);
            }
            check=temp;
            factor*=-1;
            step/=10;
        }

        if (!check){
            k+=factor*step*10;
        }

        k*=extraZoom;
        newPosition=newPosition.copy(direction).multiplyScalar(k).add(newLookAt);

        // const debugCam=new THREE.PerspectiveCamera(this.camera.fov,this.camera.aspect,this.camera.near, this.camera.far);
        // debugCam.position.copy(newPosition);
        // STAGE.scene.add(debugCam);
        // debugCam.lookAt(newLookAt);
        // debugCam.updateProjectionMatrix();
        // const aCamHelper=new THREE.CameraHelper(debugCam);
        // STAGE.scene.add(aCamHelper);
        // const aBox = Scenes.box(box.getSize(),new THREE.MeshPhongMaterial({color:0xff0000, 'wireframe':true }) );
        // aBox.position.copy(newLookAt);
        // STAGE.scene.add(aBox);
        // const coneDepth=v3(direction).multiplyScalar(k).length();
        // const cone=new THREE.Mesh(new THREE.ConeBufferGeometry(coneDepth*Math.tan(2*angleCenterRayOuterRay),coneDepth,36,1,true)
        //     ,new THREE.MeshPhongMaterial({color:0x00ff00, opacity:0.2, transparent:true }));
        // cone.position.copy(newPosition).add(v3(direction).multiplyScalar(-k*0.5));
        // const di=v3(direction).normalize();
        // const aQuat=quat().setFromUnitVectors( v3(0,1,0), di );
        // cone.applyQuaternion(aQuat);
        //
        // STAGE.scene.add(cone);
        //
        // STAGE.dirty();
        // console.log(newLookAt,newPosition,k,corners);


        return [newLookAt,newPosition];
    }


    /**
     * gibt Camera-Json-Config zurück, um ein gegebenes Objekt komplett auf dem
     * Bildschirm darzustellen
     * @param {(THREE.Object3D|THREE.Sphere|THREE.Box3|Array<THREE.Object3D>)} objectsOrSphereOrBox das Objekt, auf das gezoomt wird
     * @param {*} [direction=null] die Richtung, in der das Objekt betrachtet werden soll
     * @param {number} [extraZoom=1] ein Extra-Zoomfaktor, der nachträglich hinzukommt
     * @param {*} [up=null] wo soll Oben sein?
     * @returns JSON-Cameraconfig
     */
    getCameraConfigAsZoomTo(objectsOrSphereOrBox,direction=null,extraZoom=1,up=null) {

        const newLookAt = v3();
        const newPosition = v3();

        if (objectsOrSphereOrBox instanceof THREE.Box3){
            this.getCameraConfigAsZoomToBox(objectsOrSphereOrBox,direction,extraZoom, newPosition, newLookAt);
        }
        else {
            this.getCameraVectorsAsZoomTo(objectsOrSphereOrBox, direction, extraZoom, newPosition, newLookAt);
        }
        if (!up) {
            up = this.camera.up;
        }

        const cameraConfig = {
            object : {},
            target : {},
            up: {},
        };

        [ 'x', 'y', 'z' ].forEach(dim => {
            cameraConfig.object[dim] = newPosition[dim];
            cameraConfig.target[dim] = newLookAt[dim];
            cameraConfig.up[dim] = up[dim];
        });

        return cameraConfig;
    }

    /**
     * Zoomt auf ein gegebenes Objekt, um es komplett auf dem Bildschirm darzustellen.
     * @param {(THREE.Object3D|THREE.Sphere|THREE.Box3|Array<THREE.Object3D>)} objectsOrSphereOrBox das Objekt, auf das gezoomt wird
     * @param {*} [direction=null] die Richtung, in der das Objekt betrachtet werden soll. Darf null sein, dann wird die aktuelle Blickrichtung beibehalten.
     * @param {number} [extraZoom=1] ein Extra-Zoomfaktor, der nachträglich hinzukommt
     */
    zoomTo(objectsOrSphereOrBox,direction=null,extraZoom=1) {
        const cameraConfig = this.getCameraConfigAsZoomTo(objectsOrSphereOrBox,direction,extraZoom);
        this.orbitControl.target.copy(cameraConfig.target);
        this.orbitControl.object.position.copy(cameraConfig.object);
        this.orbitControl.update();
        this.dirty();
    }

    /**
     * zoomt ein Stück in die Szene hinein
     */
    zoomIn(zoomScale=this.zoomScale) {
        this.orbitControl.dollyOut(zoomScale);
        this.orbitControl.update();
        this.dirty();
    }

    /**
     * zoomt ein Stück heraus
     */
    zoomOut(zoomScale=this.zoomScale) {
        this.orbitControl.dollyIn(zoomScale);
        this.orbitControl.update();
        this.dirty();
    }

    /**
     * rotiert die Kamera ein Stück hoch
     */
    rotateUp(angle=-this.orbitControl.getAutoRotationAngle()) {
        this.orbitControl.rotateUp(angle);
        this.orbitControl.update();
        this.dirty();
    }

    /**
     * rotiert die Kamera ein Stück runter
     */
    rotateDown(angle=+this.orbitControl.getAutoRotationAngle()) {
        this.orbitControl.rotateUp(angle);
        this.orbitControl.update();
        this.dirty();
    }

    /**
     * rotiert die Kamera ein Stück rechts
     */
    rotateRight(angle=+this.orbitControl.getAutoRotationAngle()) {
        this.orbitControl.rotateLeft(angle);
        this.orbitControl.update();
        this.dirty();
    }

    /**
     * rotiert die Kamera ein Stück links
     */
    rotateLeft(angle=-this.orbitControl.getAutoRotationAngle()) {
        this.orbitControl.rotateLeft(angle);
        this.orbitControl.update();
        this.dirty();
    }

    /**
     * verschiebt die Kamera ein Stück nach oben
     */
    moveUp(panDistance=this.orbitControl.keyPanSpeed) {
        this.orbitControl.pan(0, -panDistance);
        this.orbitControl.update();
        this.dirty();
    }

    /**
     * verschiebt die Kamera ein Stück nach unten
     */
    moveDown(panDistance=this.orbitControl.keyPanSpeed) {
        this.orbitControl.pan(0, +panDistance);
        this.orbitControl.update();
        this.dirty();
    }

    /**
     * verschiebt die Kamera ein Stück nach rechts
     */
    moveRight(panDistance=this.orbitControl.keyPanSpeed) {
        this.orbitControl.pan(+panDistance, 0);
        this.orbitControl.update();
        this.dirty();
    }

    /**
     * verschiebt die Kamera ein Stück nach links
     */
    moveLeft(panDistance=this.orbitControl.keyPanSpeed) {
        this.orbitControl.pan(-panDistance, 0);
        this.orbitControl.update();
        this.dirty();
    }

    /**
     * setzt die Kamera auf die Defaulteinstellungen zurück
     */
    resetOrbitControl() {
        const cameraSettings = this.settings.camera;
        const orbitControl = this.orbitControl;

        orbitControl.target.copy(cameraSettings.target);
        orbitControl.object.position.copy(cameraSettings.position);

        orbitControl.minPolarAngle = Maths3.angelDegToRad(cameraSettings.polarAngle.min);
        orbitControl.maxPolarAngle = Maths3.angelDegToRad(cameraSettings.polarAngle.max);

        orbitControl.minDistance = cameraSettings.distance.min;
        orbitControl.maxDistance = cameraSettings.distance.max;
        orbitControl.autoRotateSpeed = 4;

        orbitControl.update();

        this.dirty();
    }

    //--------------------------------------------------------------------------------------
    // Background Image
    //--------------------------------------------------------------------------------------

    /**
     * setzt das Hintergrundbild der Szene. null = kein Bild
     */
    showBackgroundImage(pathToImageInContentFolder = null) {
        const showBackground = !Commons.isEmpty(pathToImageInContentFolder);

        this.trigger('showBackgroundImage',showBackground);

        if (showBackground) {
            const imgUrl = APP.settings.wkfgContentBaseUrl + pathToImageInContentFolder + "?" + new Date().getTime();
            this._setBackgroundImage(imgUrl);
        } else {
            this._removeBackgroundImage(true);
        }
    }

    _removeBackgroundImage(disposeTexture) {
        if (this.scene.background == null)
            return;

        if (disposeTexture)
            this.scene.background.dispose();

        delete this.scene.background;
        this.dirty();
    }

    _setBackgroundImage(imageUrl) {
        let loader = new THREE.TextureLoader();
        loader.load(imageUrl, this._setBackgroundTexture);
    }

    _setBackgroundTexture(texture) {
        if (texture) {
            texture.flipY = false;
        }
        STAGE.scene.background = texture;
        STAGE._backgroundFitToCover();
        STAGE.dirty();
    }

    _backgroundFitToCover() {
        let texture = STAGE.scene.background;
        if (texture) {
            let stageAspect = STAGE.canvas.width() / STAGE.canvas.height();
            let imageAspect = texture.image.width / texture.image.height;

            let xAspect = imageAspect / stageAspect;
            let yAspect = 1;

            if (xAspect > 1) {
                yAspect /= xAspect;
                xAspect = 1;
            }
            texture.repeat.x = yAspect;
            texture.repeat.y = -xAspect;

            texture.offset.x = 0.5 * (1-texture.repeat.x);
            texture.offset.y = 0.5 * (1-texture.repeat.y);

            texture.needsUpdate = true;
        }
        STAGE.dirty();
    }

    //--------------------------------------------------------------------------------------
    // Features
    //--------------------------------------------------------------------------------------

    /**
     * Fügt das Animator-Feature hinzu, entspricht settings.animator = true. Über STAGE.animator
     * können dann einfache Animationen laufen gelassen werden wie das Pulsieren von Connectoren
     * während des Draggens in der Szene. Animationen, die nur kurz Laufen wie das Öffnen einer
     * Tür sollte man dagegen eher mit TWEEN machen.
     *
     * Eine Animation wird als Zustand betrachtet, der über STAGE.animator.start('animationstag')
     * gestartet und über STAGE.animator.stop('animationstag') beendet wird. Es können mehrere
     * Animationen laufen.
     *
     * Solange eine Animation läuft, wird alle 100ms jedes Objekt der Szene, das eine Methode
     * onAnimatorTick(timestamp,animatorTags) besitzt, entsprechend aufgerufen. Das Objekt
     * ist für die Darstellung der Animation selbst verantwortlich und muss prüfen, ob und welche
     * Animation gerade für das Objekt relevant ist (also Filtern von animationTags).
     */
    addAnimator() {

        if (this.animator) {
            return;
        }

        class Animator extends ListenerMixin() {

            timer = null;
            tags = new Set();

            tick() {
                const now = Date.now();
                STAGE.trigger('animatorTick', now, this.tags);
                STAGE.scene.traverse((node) => {
                    if (node.onAnimatorTick) {
                        node.onAnimatorTick(now, this.tags);
                    }
                });
            }

            start(tag) {
                if (tag) {
                    this.tags.add(tag);
                }
                if (this.timer) {
                    return;
                }
                this.trigger('start', this.tags);
                this.timer = setInterval(()=> {
                    STAGE.dirty();
                }, 100);
            }

            stop(tag) {
                //console.log('this.stop',tag);
                if (tag) {
                    this.tags.delete(tag);
                    if (this.tags.size > 0) {
                        return;
                    }
                }
                if (this.timer) {
                    clearInterval(this.timer);
                    this.timer = null;
                    this.trigger('stop', this.tags);
                    // ein letztes Mal neu zeichnen, weil der "Animation-ist-beendet"-Zustand eventuell anders
                    // dargestellt wird
                    STAGE.dirty();
                }
            }

        }

        this.animator = new Animator();

        this.on('beforeRenderScene', ()=>this.animator.tick());

    }

    /**
     * setzt die Kamera in einen Modus, dass die hoch-runter Bewegung (Rechtsklick Maus und Mausvor-zurück)
     * die Kamera nicht in der Höhe bewegt, sondern parallel zum Boden vor- und zurück.
     * Dieser Modus ist für Planer wie Winnetoo geeignet, wo man über eine größere Boden-Szene
     * navigieren muss und nicht nur ein zentrales Objekt von allen Seiten betrachten möchte.
     * @param height die Flughöhe
     */
    bindOrbitControlToYPlaneWithHeight(height) {

        const planeY0 = Maths3.plane.y0;
        this.planeYHeight = new THREE.Plane().setFromNormalAndCoplanarPoint(Maths3.vector.y,v3(0,height,0));
        const orbit = this.orbitControl;

        const vx = v3();
        const vy = v3();
        const vz = v3();

        this.orbitControl.panUp = (distance) => {
            orbit.object.matrix.extractBasis(vx,vy,vz);
            let panOffset = vy.sub(vz.multiplyScalar(1.5));
            panOffset.multiplyScalar( distance );
            panOffset = planeY0.projectPoint(panOffset,v3());
            this.orbitControl.getPan().add(panOffset);
        }

        this.orbitControl.processTarget = (target) => {
            if (!this.ignoreBindOrbitControlToYPlaneWithHeight) {
                target.copy(this.planeYHeight.projectPoint(target),v3());
            }
        }
    }

    /**
     * Zeigt eine Info-Box mit detaillierten Angaben zum Memory und Renderer an.
     */
    showFramesPerSecondInfo() {
        const renderStatsContainer = $('<div class="renderstats"><div class="history"></div><div class="current"></div></div>');
        const infoContainer = $('.current',renderStatsContainer);
        const template = HBS('oc-threejs/renderStats.hbs');

        let appended = false;
        const stats = new Stats();
        stats.setMode(0); // 0: fps, 1: ms
        $('.history',renderStatsContainer).append(stats.domElement);

        STAGE.on('beforeRenderScene',()=>stats.begin());
        STAGE.on('afterRenderScene',()=>stats.end());
        STAGE.on('afterRenderScene', _.throttle(() => {
            if (!appended) {
                appended = true;
                STAGE.canvas.parent().append(renderStatsContainer);
            }
            infoContainer.html(template({
                info: STAGE.renderer.info,
                lastRenderSceneMs: this.lastRenderSceneMs.toFixed(2),
                quality: APP.qualityController ? APP.qualityController.qualityIndex+' '+APP.qualityController.getQualityName() : '-'
            }));
        }, 300));
    }

    composerHook() {
        if (!STAGE.settings.useAmbientOcclusion) {
            return;
        }
        STAGE.useComposer = true;

        this.composer = new THREE.EffectComposer(this.renderer);


        this.renderPass = new THREE.RenderPass(this.scene, this.camera);

        const visibleSwitchPass = new VisibleSwitchRenderPass(x => x.hideForSAOPASS);

        this.composer.addPass(this.renderPass);

        this.saoPass = new THREE.SAOPass(this.scene, this.camera, false, true);
        this.saoPass.renderToScreen = true;
        this.composer.addPass(visibleSwitchPass);
        this.composer.addPass(this.saoPass);
        this.composer.addPass(visibleSwitchPass);
        _.merge(STAGE.saoPass.params, this.settings.saoPass);



    }

    /**
     * Schaltet Schatten zur laufzeit an / aus
     */
    toggleShadowEnabled(on) {
        STAGE.renderer.shadowMap.autoUpdate = on;
        if (!on) {
            STAGE.scene.traverse(o=>{
                if (o.shadow && o.shadow.map) {
                    //STAGE.renderer.clearTarget( o.shadow.map ); DEPRECATED
                    const currentRenderTarget = STAGE.renderer.getRenderTarget();
                    STAGE.renderer.setRenderTarget(o.shadow.map);
                    STAGE.renderer.clear();
                    STAGE.renderer.setRenderTarget(currentRenderTarget);
                }
            });
        }
    }

}

